import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import FormDialog from 'src/components/DialogBox';
import PageTitle from 'src/components/PageTitle';
import { get } from 'src/services/httpClient.service';

const APIURL = process.env.REACT_APP_BASE_API_URL;

const Schedules = () => {
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openUpdateDialog, setOpenUpdateDialog] = useState(false);
  const [openPayloadDialog, setOpenPayloadDialog] = useState(false);
  const [currentPayload, setCurrentPayload] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const [taskTypeFilter, setTaskTypeFilter] = useState('ALL');
  const [scheduleTypeFilter, setScheduleTypeFilter] = useState('ALL');

  // Fetch schedules data from API
  const fetchSchedules = async () => {
    try {
      setLoading(true);
      const response = await get(`${APIURL}/api/scheduled-jobs`);
      setSchedules(response);
      setFilteredSchedules(response);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to fetch schedules');
      console.error('Error fetching schedules:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchedules();
  }, []);

  const cronOptions = [
  { label: "Every 1 minute", value: "0 0/1 * * * ?" },
  { label: "Every 5 minutes", value: "0 0/5 * * * ?" },
  { label: "Every 10 minutes", value: "0 0/10 * * * ?" },
  { label: "Every 30 minutes", value: "0 0/30 * * * ?" },
  { label: "Every 1 hour", value: "0 0 0/1 * * ?" },
  { label: "Every day", value: "0 0 0 * * ?" },
  { label: "Specific day in a week (Mon)", value: "0 0 0 ? * MON" },
  { label: "Weekends", value: "0 0 0 ? * SAT,SUN" },
  { label: "Every 15 days", value: "0 0 0 1,16 * ?" },
  { label: "Every 1 month", value: "0 0 0 1 * ?" },
  { label: "Every 3 months", value: "0 0 0 1 1,4,7,10 ?" },
  { label: "Every 6 months", value: "0 0 0 1 1,7 ?" },
  { label: "Every 1 year", value: "0 0 0 1 1 ?" }
];

// Add this function to find the matching label
const getCronLabel = (cronValue) => {
  const option = cronOptions.find(opt => opt.value === cronValue);
  return option ? option.label : cronValue; // Return the raw value if no match found
};

  // Apply filters when filters or schedules change
  useEffect(() => {
    let filtered = [...schedules];

    // Apply Task Type filter
    if (taskTypeFilter !== 'ALL') {
      filtered = filtered.filter(
        schedule => schedule.taskType === taskTypeFilter
      );
    }

    // Apply Schedule Type filter
    if (scheduleTypeFilter !== 'ALL') {
      filtered = filtered.filter(
        schedule => schedule.scheduleType === scheduleTypeFilter
      );
    }

    setFilteredSchedules(filtered);
  }, [taskTypeFilter, scheduleTypeFilter, schedules]);



/*   const editschedule = (id) => {
    setSelectedId(id);
    setOpenViewDialog(true);
  }; */

  const showPayloadDialog = (payloadString) => {
    if (!payloadString) {
      setCurrentPayload({});
      setOpenPayloadDialog(true);
      return;
    }

    try {
      const payload = JSON.parse(payloadString);
      setCurrentPayload(payload);
      setOpenPayloadDialog(true);
    } catch (error) {
      console.error('Error parsing payload:', error);
      setCurrentPayload({ error: 'Invalid payload format' });
      setOpenPayloadDialog(true);
    }
  };

  // Base columns configuration
  const baseColumns = [
    { field: 'id', headerName: 'ID', flex: 1 },
    { field: 'jobName', headerName: 'Job Name', flex: 1 },
    { field: 'taskType', headerName: 'Task Type', flex: 1 },
    { field: 'scheduleType', headerName: 'Schedule Type', flex: 1 },
    { 
      field: 'payload', 
      headerName: 'Payload', 
      flex: 2,
      renderCell: (params) => {
        if (!params.value) {
          return <Typography variant="body2" color="textSecondary">Payload null</Typography>;
        }
        return (
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => showPayloadDialog(params.value)}
          >
            View Payload
          </Button>
        );
      }
    },
    { 
      field: 'isActive', 
      headerName: 'Active', 
      flex: 1,
      type: 'boolean'
    },
    { field: 'retryCount', headerName: 'Retry Count', flex: 1 },
    { field: 'maxRetries', headerName: 'Max Retries', flex: 1 },
    
  ];

  // Conditional columns
  const conditionalColumns = {
    cron: [
      { 
      field: 'cronExpression', 
      headerName: 'Schedule', 
     
      valueFormatter: (params) => {
        if (!params.value) return 'Not set';
        return getCronLabel(params.value);
      }
    },
    ],
    datetime: [
      { 
        field: 'runAt', 
        headerName: 'Run At', 
        flex: 1,
        valueFormatter: (params) => {
          if (!params.value) return '';
          const date = new Date(params.value);
          return date.toLocaleString();
        }
      },
    ],
    interval: [
      { field: 'intervalSeconds', headerName: 'Interval (s)', flex: 1 },
    ],
  };

  // Function to get columns based on scheduleTypeFilter
  const getColumns = () => {
    if (scheduleTypeFilter === 'ALL') {
      return [
        ...baseColumns,
        { 
        field: 'cronExpression', 
        headerName: 'Schedule', 
      
        valueFormatter: (params) => {
          if (!params.value) return 'Not set';
          return getCronLabel(params.value);
        }
      },
        { field: 'intervalSeconds', headerName: 'Interval (s)', flex: 1 },
        { 
          field: 'runAt', 
          headerName: 'Run At', 
          flex: 1,
          valueFormatter: (params) => {
            if (!params.value) return '';
            const date = new Date(params.value);
            return date.toLocaleString();
          }
        },
      ];
    }

    return [
      ...baseColumns,
      ...(conditionalColumns[scheduleTypeFilter.toLowerCase()] || [])
    ];
  };

  const onClose = () => {
    fetchSchedules();
    setOpenDialog(false);
    setOpenUpdateDialog(false);
  };

  const handleClick = () => {
    setOpenDialog(true);
  };

  return (
    <Box p={3}>
      <PageTitle
        heading="Finger Print Schedule"
        subHeading=""
        handleClick={handleClick}
        /* label="Add Schedule" */
      />

      {openDialog && (
        <FormDialog
          open={openDialog}
          onClose={onClose}
          title="Add Schedule"
          component="addschedule"
          disableEscapeKeyDown
          disableBackdropClick 
        />
      )}

      {/* Filters */}
      <Grid container spacing={2} mb={2} sx={{marginTop:"20px"}}>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Task Type</InputLabel>
            <Select
              value={taskTypeFilter}
              label="Task Type"
              onChange={(e) => setTaskTypeFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Task Types</MenuItem>
              <MenuItem value="GLOBAL">GLOBAL</MenuItem>
              <MenuItem value="SINGLE">SINGLE</MenuItem>
              <MenuItem value="CHANNEL">CHANNEL</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Schedule Type</InputLabel>
            <Select
              value={scheduleTypeFilter}
              label="Schedule Type"
              onChange={(e) => setScheduleTypeFilter(e.target.value)}
            >
              <MenuItem value="ALL">All Schedule Types</MenuItem>
              <MenuItem value="cron">CRON</MenuItem>
              <MenuItem value="datetime">DATETIME</MenuItem>
              <MenuItem value="interval">INTERVAL</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Payload Dialog */}
      <Box sx={{'& .MuiPaper-root':{width:'200px'}}}>
        <Dialog 
          open={openPayloadDialog} 
          onClose={() => setOpenPayloadDialog(false)} 
          maxWidth="md"
        >
          <DialogTitle>Payload Details</DialogTitle>
          <DialogContent>
            {Object.keys(currentPayload).length === 0 ? (
              <Typography variant="body1" align="center" sx={{ p: 2 }}>
                Payload is null or empty
              </Typography>
            ) : (
              <TableContainer component={Paper} sx={{ maxHeight: 400, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Setting</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(currentPayload).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell sx={{ fontWeight: 'bold' }}>{key}</TableCell>
                        <TableCell>
                          {typeof value === 'boolean' ? 
                            (value ? 'Yes' : 'No') : 
                            String(value)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenPayloadDialog(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
      
      {/* DataGrid */}
      <Box mt={2} sx={{ height: 'calc(100vh - 200px)', width: '100%',overflow:"auto" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <DataGrid
            rows={filteredSchedules}
            columns={getColumns()}
            pageSize={pageSize}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            rowsPerPageOptions={[10, 20]}
            checkboxSelection
            disableSelectionOnClick
          />
        )}
      </Box>
    </Box>
  );
};

export default Schedules;